/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaStar } from 'react-icons/fa';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * CustomerReviewForm - Public form for customers to review driver and platform
 * Accessible via /review-driver/:jobId
 */
export default function CustomerReviewForm() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [reviewExists, setReviewExists] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: ''
  });
  
  // Driver ratings
  const [helpful, setHelpful] = useState(5);
  const [informative, setInformative] = useState(5);
  const [carCondition, setCarCondition] = useState(5);
  const [driverComments, setDriverComments] = useState('');
  
  // Platform ratings
  const [easyToUse, setEasyToUse] = useState(5);
  const [gotTaxi, setGotTaxi] = useState(5);
  const [communication, setCommunication] = useState(5);
  const [platformComments, setPlatformComments] = useState('');
  
  // Hover states for stars
  const [hoverStates, setHoverStates] = useState({});

  useEffect(() => {
    loadJobData();
  }, [jobId]);

  const loadJobData = async () => {
    try {
      setLoading(true);
      
      // Check if review already exists
      const reviewCheck = await axios.get(`/driver-reviews/job/${jobId}`);
      if (reviewCheck.data.exists) {
        setReviewExists(true);
        setLoading(false);
        return;
      }
      
      // Load job data (public endpoint not available, so we'll collect customer info in form)
      setLoading(false);
    } catch (err) {
      console.error('Error loading job:', err);
      toast.error('Error loading job information');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName.trim() || !formData.customerEmail.trim() || !formData.customerPhone.trim()) {
      toast.error('Please fill in all your contact information');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post('/driver-reviews', {
        jobId,
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        driverRatings: {
          helpful,
          informative,
          carCondition
        },
        platformRatings: {
          easyToUse,
          gotTaxi,
          communication
        },
        driverComments: driverComments.trim(),
        platformComments: platformComments.trim()
      });
      
      toast.success('Thank you for your review!');
      setTimeout(() => {
        navigate('/thank-you');
      }, 2000);
    } catch (err) {
      console.error('Error submitting review:', err);
      toast.error(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, setRating, label) => {
    const hoverValue = hoverStates[label] || 0;
    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{label}</strong>
          <div className="d-flex align-items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={28}
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                color={(hoverValue || rating) >= star ? '#ffc107' : '#e4e5e9'}
                onMouseEnter={() => setHoverStates(prev => ({ ...prev, [label]: star }))}
                onMouseLeave={() => setHoverStates(prev => ({ ...prev, [label]: 0 }))}
                onClick={() => setRating(star)}
              />
            ))}
            <span className="ms-2" style={{ fontWeight: 'bold', minWidth: '40px' }}>
              {rating}/5
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading review form...</p>
      </Container>
    );
  }

  if (reviewExists) {
    return (
      <Container className="py-5">
        <Card>
          <Card.Body className="text-center py-5">
            <h3 className="mb-3">✅ Review Already Submitted</h3>
            <p className="text-muted">
              Thank you! You have already submitted a review for this journey.
            </p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: '#007bff', color: 'white' }}>
          <h4 className="mb-0">⭐ Rate Your Journey with Taxi-Local</h4>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <strong>Thank you for choosing Taxi-Local!</strong> Your feedback helps us improve our service and helps other drivers maintain high standards.
          </Alert>

          <Form onSubmit={handleSubmit}>
            {/* Customer Information */}
            <div className="mb-4">
              <h5 className="mb-3">📋 Your Information</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Name *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Your name"
                      value={formData.customerName}
                      onChange={(e) => setFormData(f => ({ ...f, customerName: e.target.value }))}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Email *</strong></Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.customerEmail}
                      onChange={(e) => setFormData(f => ({ ...f, customerEmail: e.target.value }))}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Phone *</strong></Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="01234567890"
                      value={formData.customerPhone}
                      onChange={(e) => setFormData(f => ({ ...f, customerPhone: e.target.value }))}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <hr />

            {/* Driver Ratings */}
            <div className="mb-4">
              <h5 className="mb-3">🚕 Rate Your Driver</h5>
              {renderStars(helpful, setHelpful, '😊 Helpful & Friendly')}
              {renderStars(informative, setInformative, '💬 Informative')}
              {renderStars(carCondition, setCarCondition, '🚗 Car Condition')}
              
              <Form.Group className="mt-3">
                <Form.Label><strong>Additional Comments about Driver</strong> <small className="text-muted">(optional)</small></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={driverComments}
                  onChange={(e) => setDriverComments(e.target.value)}
                  placeholder="Tell us about your experience with the driver..."
                  maxLength={500}
                />
                <small className="text-muted">{driverComments.length}/500</small>
              </Form.Group>
            </div>

            <hr />

            {/* Platform Ratings */}
            <div className="mb-4">
              <h5 className="mb-3">📱 Rate Taxi-Local System</h5>
              {renderStars(easyToUse, setEasyToUse, '👍 Easy to Use')}
              {renderStars(gotTaxi, setGotTaxi, '🚕 Got Taxi Successfully')}
              {renderStars(communication, setCommunication, '📞 Communication with Driver')}
              
              <Form.Group className="mt-3">
                <Form.Label><strong>Additional Comments about Taxi-Local</strong> <small className="text-muted">(optional)</small></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={platformComments}
                  onChange={(e) => setPlatformComments(e.target.value)}
                  placeholder="Tell us about your experience using Taxi-Local..."
                  maxLength={500}
                />
                <small className="text-muted">{platformComments.length}/500</small>
              </Form.Group>
            </div>

            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                variant="success" 
                size="lg"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Submitting Review...
                  </>
                ) : (
                  <>⭐ Submit Review</>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Alert variant="secondary" className="text-center">
        <small>
          All ratings default to 5 stars. Click on stars to change if needed.<br />
          Your feedback is valuable and helps improve our service. Thank you!
        </small>
      </Alert>
    </Container>
  );
}

